import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Unstable_Grid2";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { styled, keyframes } from "@mui/material/styles";
import SynthiumLogoLoader from "../static/POET_Logo_Pen.png";

// ── Feature icons ─────────────────────────────────────────────────────────────
import FolderOpenIcon        from "@mui/icons-material/FolderOpen";
import ShareIcon             from "@mui/icons-material/Share";
import DownloadIcon          from "@mui/icons-material/Download";
import SpellcheckIcon        from "@mui/icons-material/Spellcheck";
import ArticleOutlinedIcon   from "@mui/icons-material/ArticleOutlined";
import GroupsIcon            from "@mui/icons-material/Groups";
import PersonAddIcon         from "@mui/icons-material/PersonAdd";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import FindInPageIcon        from "@mui/icons-material/FindInPage";
import AutoFixHighIcon       from "@mui/icons-material/AutoFixHigh";
import SummarizeIcon         from "@mui/icons-material/Summarize";
import TranslateIcon         from "@mui/icons-material/Translate";
import FilterAltIcon         from "@mui/icons-material/FilterAlt";
import VideoCallIcon         from "@mui/icons-material/VideoCall";
import CloudUploadIcon       from "@mui/icons-material/CloudUpload";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import PeopleAltIcon         from "@mui/icons-material/PeopleAlt";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import AutoAwesomeIcon       from "@mui/icons-material/AutoAwesome";
import LibraryBooksIcon      from "@mui/icons-material/LibraryBooks";
import SecurityIcon          from "@mui/icons-material/Security";
import TrendingUpIcon        from "@mui/icons-material/TrendingUp";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import HistoryIcon           from "@mui/icons-material/History";
import BoltIcon              from "@mui/icons-material/Bolt";
import BarChartIcon          from "@mui/icons-material/BarChart";
import EditIcon              from "@mui/icons-material/Edit";
import FormatQuoteIcon       from "@mui/icons-material/FormatQuote";
import TextFieldsIcon        from "@mui/icons-material/TextFields";

// ── Timing ────────────────────────────────────────────────────────────────────
const DISPLAY_MS = 2800;
const FADE_MS    = 380;

// ── Screen data ───────────────────────────────────────────────────────────────
const SCREENS = {
  "/coauthor": {
    label:    "AI Writing Workspace",
    accent:   "#4F46E5",
    gradient: "linear-gradient(135deg,#4F46E5 0%,#7C3AED 100%)",
    features: [
      { Icon: FolderOpenIcon,         color: "#4F46E5", bg: "#EEF2FF", title: "Smart Folders",          desc: "Organize every document into folders for fast, structured access" },
      { Icon: ShareIcon,              color: "#0891B2", bg: "#ECFEFF", title: "Real-Time Sharing",      desc: "Invite teammates and co-edit documents together in real time"    },
      { Icon: ArticleOutlinedIcon,    color: "#7C3AED", bg: "#F3E8FF", title: "20+ Templates",          desc: "Kickstart any project with professional ready-to-use templates"   },
      { Icon: SpellcheckIcon,         color: "#059669", bg: "#ECFDF5", title: "Grammar Check Built-In", desc: "Catch errors and improve your writing quality with one click"     },
      { Icon: DownloadIcon,           color: "#D97706", bg: "#FFFBEB", title: "Export Anywhere",        desc: "Download your work as PDF or Word document instantly"            },
    ],
  },
  "/mydocuments": {
    label:    "SmartDoc Library",
    accent:   "#4F46E5",
    gradient: "linear-gradient(135deg,#4F46E5 0%,#06B6D4 100%)",
    features: [
      { Icon: FilterAltIcon,          color: "#4F46E5", bg: "#EEF2FF", title: "Advanced Filters",    desc: "Slice your library by status, date, owner or tags in seconds"       },
      { Icon: CloudUploadIcon,        color: "#0891B2", bg: "#ECFEFF", title: "Upload Any Format",   desc: "Import PDF, DOCX, DOC, or plain-text files without friction"       },
      { Icon: AssignmentTurnedInIcon, color: "#059669", bg: "#ECFDF5", title: "Status Tracking",     desc: "Follow every document from Open to Completed at a glance"           },
      { Icon: ShareIcon,              color: "#7C3AED", bg: "#F3E8FF", title: "Instant Sharing",     desc: "Share documents and folders with team members in a single click"   },
      { Icon: DashboardCustomizeIcon, color: "#D97706", bg: "#FFFBEB", title: "Grid & List Views",   desc: "Switch layouts to browse your library the way you like"             },
    ],
  },
  "/myteam": {
    label:    "Team Hub",
    accent:   "#0D9488",
    gradient: "linear-gradient(135deg,#0D9488 0%,#4F46E5 100%)",
    features: [
      { Icon: PersonAddIcon,          color: "#0D9488", bg: "#F0FDFA", title: "Invite Members",      desc: "Add teammates by email and assign roles in just a few clicks"     },
      { Icon: GroupsIcon,             color: "#4F46E5", bg: "#EEF2FF", title: "Manage Groups",        desc: "Create groups for departments, projects, or workflows"             },
      { Icon: PeopleAltIcon,          color: "#7C3AED", bg: "#F3E8FF", title: "Live Collaboration",   desc: "See who's online and co-author documents side by side"             },
      { Icon: BarChartIcon,           color: "#059669", bg: "#ECFDF5", title: "Team Analytics",       desc: "Track activity and document contributions across your team"        },
      { Icon: NotificationsActiveIcon,color: "#D97706", bg: "#FFFBEB", title: "Smart Notifications", desc: "Stay on top of reviews, approvals, and team activity"               },
    ],
  },
  "/chat": {
    label:    "Chat",
    accent:   "#7C3AED",
    gradient: "linear-gradient(135deg,#7C3AED 0%,#06B6D4 100%)",
    features: [
      { Icon: ChatBubbleOutlineIcon,  color: "#7C3AED", bg: "#F3E8FF", title: "Direct Messages",     desc: "Send private messages to any team member instantly"                 },
      { Icon: GroupsIcon,             color: "#4F46E5", bg: "#EEF2FF", title: "Group Channels",       desc: "Create dedicated channels for teams, projects, or topics"           },
      { Icon: VideoCallIcon,          color: "#0891B2", bg: "#ECFEFF", title: "Video & Audio Calls", desc: "Start voice or video calls directly from any conversation"           },
      { Icon: CloudUploadIcon,        color: "#059669", bg: "#ECFDF5", title: "File Sharing",         desc: "Drop PDFs, images, and documents into any chat thread"             },
      { Icon: NotificationsActiveIcon,color: "#D97706", bg: "#FFFBEB", title: "Real-Time Alerts",     desc: "Never miss a message with smart push notifications"                 },
    ],
  },
  "/grammarcheck": {
    label:    "Grammar Check",
    accent:   "#2563EB",
    gradient: "linear-gradient(135deg,#2563EB 0%,#059669 100%)",
    features: [
      { Icon: SpellcheckIcon,         color: "#2563EB", bg: "#EFF6FF", title: "AI Error Detection",   desc: "Catch grammar, spelling, and punctuation mistakes instantly"       },
      { Icon: AutoAwesomeIcon,        color: "#7C3AED", bg: "#F3E8FF", title: "Style Suggestions",    desc: "Get AI recommendations to sharpen your tone and clarity"           },
      { Icon: TextFieldsIcon,         color: "#059669", bg: "#ECFDF5", title: "Readability Score",    desc: "Understand how readable your writing is at a glance"               },
      { Icon: HistoryIcon,            color: "#D97706", bg: "#FFFBEB", title: "Track Changes",        desc: "Review every correction with a full, auditable change history"     },
      { Icon: DownloadIcon,           color: "#0891B2", bg: "#ECFEFF", title: "Export Report",        desc: "Download a complete grammar report as PDF or Word"                 },
    ],
  },
  "/plagiarism": {
    label:    "Plagiarism Checker",
    accent:   "#DC2626",
    gradient: "linear-gradient(135deg,#DC2626 0%,#7C3AED 100%)",
    features: [
      { Icon: FindInPageIcon,         color: "#DC2626", bg: "#FEF2F2", title: "Originality Score",    desc: "Instantly see the exact percentage of original content"             },
      { Icon: SecurityIcon,           color: "#7C3AED", bg: "#F3E8FF", title: "Deep Web Scan",        desc: "Cross-check your text against millions of online sources"           },
      { Icon: TrendingUpIcon,         color: "#059669", bg: "#ECFDF5", title: "Source Highlights",    desc: "See exactly which sources match your content passages"              },
      { Icon: AutoFixHighIcon,        color: "#D97706", bg: "#FFFBEB", title: "Rewrite Suggestions",  desc: "Get AI-powered rewrites for any flagged passages"                   },
      { Icon: DownloadIcon,           color: "#0891B2", bg: "#ECFEFF", title: "Detailed Report",      desc: "Export a comprehensive plagiarism report in one click"              },
    ],
  },
  "/paraphrase": {
    label:    "Paraphraser",
    accent:   "#7C3AED",
    gradient: "linear-gradient(135deg,#7C3AED 0%,#DC2626 100%)",
    features: [
      { Icon: AutoFixHighIcon,        color: "#7C3AED", bg: "#F3E8FF", title: "Smart Rewriting",      desc: "Rephrase any text while preserving its original meaning"            },
      { Icon: BoltIcon,               color: "#D97706", bg: "#FFFBEB", title: "Multiple Tones",       desc: "Choose Standard, Fluency, Creative, or Formal mode"                },
      { Icon: FormatQuoteIcon,        color: "#4F46E5", bg: "#EEF2FF", title: "Bulk Paraphrase",      desc: "Rewrite entire paragraphs or full documents at once"                },
      { Icon: SpellcheckIcon,         color: "#059669", bg: "#ECFDF5", title: "Grammar Safe",         desc: "Every paraphrased output is automatically error-checked"            },
      { Icon: DownloadIcon,           color: "#0891B2", bg: "#ECFEFF", title: "Export Results",       desc: "Copy or download your paraphrased content instantly"                },
    ],
  },
  "/Summarizer": {
    label:    "Summarizer",
    accent:   "#059669",
    gradient: "linear-gradient(135deg,#059669 0%,#0891B2 100%)",
    features: [
      { Icon: SummarizeIcon,          color: "#059669", bg: "#ECFDF5", title: "Key Takeaways",        desc: "Extract the most important points from any document"                },
      { Icon: BoltIcon,               color: "#D97706", bg: "#FFFBEB", title: "Adjustable Length",    desc: "Control how short or detailed your summary should be"               },
      { Icon: AutoAwesomeIcon,        color: "#7C3AED", bg: "#F3E8FF", title: "Bullet Summaries",     desc: "Get clean, structured bullet-point summaries at a glance"           },
      { Icon: LibraryBooksIcon,       color: "#4F46E5", bg: "#EEF2FF", title: "Multi-Format Input",   desc: "Summarize PDFs, Word docs, and plain text effortlessly"             },
      { Icon: DownloadIcon,           color: "#0891B2", bg: "#ECFEFF", title: "Export Summary",       desc: "Save or share your summary in any format with one click"            },
    ],
  },
  "/translator": {
    label:    "Translator",
    accent:   "#D97706",
    gradient: "linear-gradient(135deg,#D97706 0%,#059669 100%)",
    features: [
      { Icon: TranslateIcon,          color: "#D97706", bg: "#FFFBEB", title: "100+ Languages",       desc: "Translate content accurately into over 100 world languages"         },
      { Icon: BoltIcon,               color: "#4F46E5", bg: "#EEF2FF", title: "Instant Translation",  desc: "See real-time translations appear as you type"                      },
      { Icon: AutoAwesomeIcon,        color: "#7C3AED", bg: "#F3E8FF", title: "Context-Aware AI",     desc: "AI preserves meaning, tone, and context across every language"      },
      { Icon: DownloadIcon,           color: "#059669", bg: "#ECFDF5", title: "Export Translated",    desc: "Save translated documents in any format you need"                   },
      { Icon: LibraryBooksIcon,       color: "#0891B2", bg: "#ECFEFF", title: "Document Upload",      desc: "Upload whole documents for fast, bulk translation"                  },
    ],
  },
  "/mynewdocuments": {
    label:    "Document Editor",
    accent:   "#4F46E5",
    gradient: "linear-gradient(135deg,#4F46E5 0%,#059669 100%)",
    features: [
      { Icon: EditIcon,               color: "#4F46E5", bg: "#EEF2FF", title: "Rich Text Editor",     desc: "Write, format, and structure your document with full control"      },
      { Icon: AutoAwesomeIcon,        color: "#7C3AED", bg: "#F3E8FF", title: "AI Writing Assistant", desc: "Get intelligent suggestions directly while you write"                },
      { Icon: ShareIcon,              color: "#0891B2", bg: "#ECFEFF", title: "Live Collaboration",   desc: "Invite teammates to co-edit the same document in real time"         },
      { Icon: HistoryIcon,            color: "#D97706", bg: "#FFFBEB", title: "Version History",      desc: "Restore any previous version of your document anytime"              },
      { Icon: DownloadIcon,           color: "#059669", bg: "#ECFDF5", title: "One-Click Export",     desc: "Download your document as PDF or Word document instantly"           },
    ],
  },
};

const DEFAULT_SCREEN = {
  label:    "Synthium AI Writing Suite",
  accent:   "#4F46E5",
  gradient: "linear-gradient(135deg,#4F46E5 0%,#7C3AED 100%)",
  features: [
    { Icon: AutoAwesomeIcon,        color: "#4F46E5", bg: "#EEF2FF", title: "AI-Powered Writing",   desc: "Enhance every document with intelligent AI assistance"              },
    { Icon: SpellcheckIcon,         color: "#059669", bg: "#ECFDF5", title: "Grammar Check",         desc: "Fix errors and improve writing quality in seconds"                  },
    { Icon: FindInPageIcon,         color: "#DC2626", bg: "#FEF2F2", title: "Plagiarism Checker",    desc: "Ensure your content is 100 % original and authentic"                },
    { Icon: SummarizeIcon,          color: "#D97706", bg: "#FFFBEB", title: "Smart Summarizer",      desc: "Extract key insights from any document instantly"                   },
    { Icon: PeopleAltIcon,          color: "#0D9488", bg: "#F0FDFA", title: "Team Collaboration",    desc: "Work together in real time with your entire team"                   },
  ],
};

// ── Animations ────────────────────────────────────────────────────────────────
const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1);    }
  50%       { opacity: .75; transform: scale(1.06); }
`;

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position:  400px 0; }
`;

const gradientShift = keyframes`
  0%,100% { background-position: 0%   50%; }
  50%      { background-position: 100% 50%; }
`;

// ── Styled components ─────────────────────────────────────────────────────────
const PulsingLogo = styled("img")({
  width:  36,
  height: 36,
  animation: `${pulse} 1.6s ease-in-out infinite`,
  filter: "drop-shadow(0 4px 12px rgba(99,102,241,.35))",
});

const RotatingLogo = styled("img")({
  width:  36,
  height: 36,
  animation: `${rotate} 2s linear infinite`,
});

const ShimmerBar = styled(Box)({
  height: 3,
  width: "100%",
  borderRadius: 2,
  background:
    "linear-gradient(90deg,rgba(79,70,229,0) 25%,rgba(79,70,229,.18) 50%,rgba(79,70,229,0) 75%)",
  backgroundSize: "400px 100%",
  animation: `${shimmer} 2s infinite linear`,
});

const accentBarSx = (gradient) => ({
  height: 3,
  width: "100%",
  borderRadius: "0 0 2px 2px",
  background: gradient,
  backgroundSize: "200% 200%",
  animation: `${gradientShift} 4s ease infinite`,
});

// ── Helpers ───────────────────────────────────────────────────────────────────
function resolveScreen() {
  const path = window.location.pathname;
  const key  = Object.keys(SCREENS).find((k) => path.startsWith(k));
  return key ? SCREENS[key] : DEFAULT_SCREEN;
}

// ── Main Loader ───────────────────────────────────────────────────────────────
const Loader = ({ loading, online }) => {
  const screen = resolveScreen();
  const features = screen.features;

  const [featureIdx, setFeatureIdx] = useState(0);
  const [visible,    setVisible]    = useState(true);
  const [progress,   setProgress]   = useState(6);

  // Reset when loading starts / stops
  useEffect(() => {
    if (!loading) { setFeatureIdx(0); setProgress(6); setVisible(true); return; }

    // Feature cycling
    const cycleTimer = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setFeatureIdx((i) => (i + 1) % features.length);
        setVisible(true);
      }, FADE_MS);
    }, DISPLAY_MS);

    // Progress crawl (stalls at 88 to never "complete" prematurely)
    const progTimer = setInterval(() => {
      setProgress((p) => (p >= 88 ? 88 : p + Math.random() * 7));
    }, 550);

    return () => { clearInterval(cycleTimer); clearInterval(progTimer); };
  }, [loading, features.length]);

  if (!loading) return null;

  const { Icon, color, bg, title, desc } = features[featureIdx];

  // ── Lost-connection card ──────────────────────────────────────────────────
  if (online) {
    return (
      <Overlay>
        <Card sx={{ maxWidth: 300, bgcolor: "#1565c0", color: "white", borderRadius: 3, boxShadow: "0 20px 60px rgba(0,0,0,.45)" }}>
          <CardContent sx={{ p: 2.25 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid xs={2}><ErrorOutlineIcon sx={{ fontSize: 36 }} /></Grid>
              <Grid xs={10}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: .5 }}>Lost Connection</Typography>
                <Typography variant="body2" sx={{ opacity: .85 }}>Your internet session was interrupted. Reconnecting…</Typography>
              </Grid>
            </Grid>
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2.5 }}>
              <RotatingLogo src={SynthiumLogoLoader} alt="Reconnecting" />
            </Box>
          </CardContent>
        </Card>
      </Overlay>
    );
  }

  // ── Normal loading card ───────────────────────────────────────────────────
  return (
    <Overlay>
      <Box
        sx={{
          display:       "flex",
          flexDirection: "column",
          width:         { xs: "80vw", sm: 300 },
          borderRadius:  "16px",
          overflow:      "hidden",
          bgcolor:       "#FFFFFF",
          border:        "1px solid #E8ECF4",
          boxShadow:     "0 20px 48px rgba(15,23,42,.15), 0 3px 14px rgba(15,23,42,.07)",
        }}
      >
        {/* Gradient accent strip */}
        <Box sx={accentBarSx(screen.gradient)} />

        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.1, px: 2.25, pt: 1.75, pb: 1.1 }}>
          <PulsingLogo src={SynthiumLogoLoader} alt="Loading" />
          <Box>
            <Typography sx={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 1.6, color: "#94A3B8", textTransform: "uppercase", lineHeight: 1 }}>
              Loading
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0F172A", lineHeight: 1.3, mt: .3 }}>
              {screen.label}
            </Typography>
          </Box>
        </Box>

        <Divider />

        {/* Feature showcase */}
        <Box sx={{ px: 2.25, pt: 1.75, pb: 1.5, minHeight: 138 }}>
          <Box
            sx={{
              transition: `opacity ${FADE_MS}ms ease, transform ${FADE_MS}ms ease`,
              opacity:   visible ? 1 : 0,
              transform: visible ? "translateY(0)" : "translateY(10px)",
            }}
          >
            {/* Icon circle */}
            <Box
              sx={{
                width:          50,
                height:         50,
                borderRadius:   "50%",
                bgcolor:        bg,
                display:        "flex",
                alignItems:     "center",
                justifyContent: "center",
                mx:             "auto",
                mb:             1.25,
                boxShadow:      `0 6px 16px ${color}35`,
                "& svg": { fontSize: 24, color },
              }}
            >
              <Icon />
            </Box>

            {/* Title */}
            <Typography
              sx={{ fontSize: 14, fontWeight: 700, color: "#0F172A", textAlign: "center", mb: .5, letterSpacing: "-.2px" }}
            >
              {title}
            </Typography>

            {/* Description */}
            <Typography
              sx={{ fontSize: 11.5, color: "#475569", textAlign: "center", lineHeight: 1.5, px: 0.5 }}
            >
              {desc}
            </Typography>
          </Box>
        </Box>

        {/* Navigation dots */}
        <Box sx={{ display: "flex", justifyContent: "center", gap: 0.75, pb: 1.75 }}>
          {features.map((_, i) => (
            <Box
              key={i}
              sx={{
                width:        i === featureIdx ? 16 : 5,
                height:       5,
                borderRadius: 8,
                bgcolor:      i === featureIdx ? screen.accent : "#D1DCF0",
                transition:   "all .35s ease",
                cursor:       "default",
              }}
            />
          ))}
        </Box>

        <Divider />

        {/* Progress */}
        <Box sx={{ px: 2.25, pt: 1.25, pb: 1.75 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: .6 }}>
            <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: "#94A3B8", letterSpacing: 1.2, textTransform: "uppercase" }}>
              {Math.round(progress)}% ready
            </Typography>
            <Typography sx={{ fontSize: 9.5, color: "#B8C4D6", fontWeight: 500 }}>
              {featureIdx + 1} / {features.length} tips
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 4, borderRadius: 4,
              bgcolor: "#EEF2FF",
              "& .MuiLinearProgress-bar": {
                borderRadius: 4,
                background:   screen.gradient,
                backgroundSize: "200% 200%",
                animation:    `${gradientShift} 4s ease infinite`,
              },
            }}
          />
          <ShimmerBar sx={{ mt: 0.75 }} />
        </Box>
      </Box>
    </Overlay>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────────
const Overlay = ({ children }) => (
  <Box
    sx={{
      position:        "fixed",
      inset:           0,
      bgcolor:         "rgba(15,23,42,.38)",
      backdropFilter:  "blur(6px)",
      zIndex:          9999,
      display:         "flex",
      alignItems:      "center",
      justifyContent:  "center",
    }}
  >
    {children}
  </Box>
);

const Divider = () => (
  <Box sx={{ height: 1, bgcolor: "#F0F4F8" }} />
);

export default Loader;
